import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Container, Grid, Card, Icon, AppBar, Toolbar } from "@mui/material";
import { fadeInUp } from "../../utils/animations";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PageLayout from "examples/LayoutContainers/PageLayout";

import IconImage from "assets/images/logo.png";

const sections = [
  {
    icon: "play_circle",
    title: "Prise en main",
    items: [
      {
        q: "Comment créer un compte ?",
        a: "Contactez votre administrateur institutionnel pour obtenir vos identifiants. Une fois reçus, connectez-vous via la page de connexion.",
      },
      {
        q: "Comment me connecter ?",
        a: 'Depuis la page d\'accueil, cliquez sur "Se connecter", saisissez votre email et mot de passe, puis validez.',
      },
      {
        q: "J\u2019ai oublié mon mot de passe",
        a: 'Cliquez sur "Mot de passe oublié" sur la page de connexion. Un email de réinitialisation vous sera envoyé.',
      },
    ],
  },
  {
    icon: "library_books",
    title: "Base documentaire",
    items: [
      {
        q: "Comment rechercher un texte ?",
        a: "Utilisez la barre de recherche par mots-clés, ou filtrez par domaine et type d\u2019acte via les menus déroulants.",
      },
      {
        q: "Comment télécharger un document ?",
        a: "Sur la fiche d\u2019un texte, cliquez sur le bouton de téléchargement à côté du document souhaité.",
      },
      {
        q: "Les documents sont-ils à jour ?",
        a: "Oui. La base est mise à jour régulièrement par les administrateurs. Vous pouvez consulter l\u2019historique des versions de chaque texte.",
      },
    ],
  },
  {
    icon: "notifications_active",
    title: "Notifications & Veille",
    items: [
      {
        q: "Comment recevoir des notifications ?",
        a: "Les notifications sont automatiques. Vous êtes alerté dès qu\u2019un nouveau texte est ajouté ou modifié dans votre périmètre.",
      },
      {
        q: "Où consulter mes notifications ?",
        a: 'Accédez à la section "Notifications" dans le menu latéral. Les notifications non lues sont mises en évidence.',
      },
    ],
  },
];

function Documentation() {
  return (
    <PageLayout>
      <Helmet>
        <title>Documentation - ConformIMF</title>
        <meta
          name="description"
          content="Consultez la documentation de ConformIMF : guides de prise en main, FAQ et ressources pour piloter votre conformité réglementaire UEMOA."
        />
        <meta property="og:title" content="Documentation - ConformIMF" />
        <meta
          property="og:description"
          content="Guides de prise en main, FAQ et ressources pour piloter votre conformité réglementaire UEMOA avec ConformIMF."
        />
        <meta property="og:url" content="https://conformimf.com/documentation" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Documentation - ConformIMF" />
        <meta
          name="twitter:description"
          content="Guides de prise en main, FAQ et ressources pour piloter votre conformité réglementaire UEMOA avec ConformIMF."
        />
      </Helmet>

      <AppBar
        position="fixed"
        sx={{
          backgroundColor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px)",
          boxShadow: "0 2px 20px rgba(0,0,0,0.08)",
          py: 0.5,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: "space-between", px: "0 !important" }}>
            <MDBox
              component={Link}
              to="/"
              display="flex"
              alignItems="center"
              sx={{ textDecoration: "none" }}
            >
              <MDBox component="img" src={IconImage} alt="Logo" width="200px" sx={{ mr: 1.5 }} />
            </MDBox>
            <MDButton
              variant="gradient"
              color="purple"
              size="small"
              component={Link}
              to="/"
              sx={{
                px: 3,
                transition: "transform 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
            >
              <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
              Accueil
            </MDButton>
          </Toolbar>
        </Container>
      </AppBar>

      <MDBox sx={{ pt: 12, pb: 6, minHeight: "100vh", backgroundColor: "#fafafa" }}>
        <Container maxWidth="lg">
          <MDBox textAlign="center" mb={6} sx={{ animation: `${fadeInUp} 0.6s ease-out both` }}>
            <Icon sx={{ fontSize: "48px !important", color: "#186A81", mb: 2 }}>menu_book</Icon>
            <MDTypography variant="h2" fontWeight="bold" mb={1}>
              Documentation
            </MDTypography>
            <MDTypography
              variant="body1"
              color="text"
              sx={{ maxWidth: 600, mx: "auto", opacity: 0.8 }}
            >
              Guide d&apos;utilisation de la plateforme ConformIMF. Retrouvez les réponses aux
              questions fréquentes.
            </MDTypography>
          </MDBox>

          <Grid container spacing={4}>
            {sections.map((section, si) => (
              <Grid item xs={12} md={6} key={si}>
                <Card
                  sx={{
                    p: 3.5,
                    height: "100%",
                    borderRadius: "16px",
                    animation: `${fadeInUp} 0.5s ease-out ${0.1 + si * 0.12}s both`,
                    transition: "transform 0.3s, box-shadow 0.3s",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 8px 25px rgba(123,31,162,0.1)",
                    },
                  }}
                >
                  <MDBox display="flex" alignItems="center" mb={2.5}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      width="45px"
                      height="45px"
                      borderRadius="12px"
                      sx={{
                        background: "linear-gradient(135deg, #186A81, #0A3475)",
                        mr: 1.5,
                      }}
                    >
                      <Icon sx={{ color: "#FFD740", fontSize: "24px" }}>{section.icon}</Icon>
                    </MDBox>
                    <MDTypography variant="h5" fontWeight="bold">
                      {section.title}
                    </MDTypography>
                  </MDBox>

                  {section.items.map((item, qi) => (
                    <MDBox
                      key={qi}
                      sx={{
                        py: 1.5,
                        borderBottom: qi < section.items.length - 1 ? "1px solid #f0f0f0" : "none",
                      }}
                    >
                      <MDBox display="flex" alignItems="flex-start" mb={0.5}>
                        <Icon
                          sx={{ color: "#186A81", fontSize: "18px", mr: 1, mt: 0.2, flexShrink: 0 }}
                        >
                          help_outline
                        </Icon>
                        <MDTypography variant="body2" fontWeight="bold">
                          {item.q}
                        </MDTypography>
                      </MDBox>
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{ pl: 3.5, opacity: 0.8, lineHeight: 1.7 }}
                      >
                        {item.a}
                      </MDTypography>
                    </MDBox>
                  ))}
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* CTA */}
          <MDBox
            textAlign="center"
            mt={6}
            sx={{ animation: `${fadeInUp} 0.6s ease-out 0.6s both` }}
          >
            <Card
              sx={{
                p: 4,
                borderRadius: "16px",
                background: "linear-gradient(135deg, #0A3475, #186A81)",
              }}
            >
              <MDTypography variant="h5" color="white" fontWeight="bold" mb={1}>
                Besoin d&apos;aide supplémentaire ?
              </MDTypography>
              <MDTypography variant="body2" color="white" sx={{ opacity: 0.85, mb: 3 }}>
                Contactez notre équipe de support pour toute question non couverte par cette
                documentation.
              </MDTypography>
              <MDButton
                variant="contained"
                component={Link}
                to="/contact"
                sx={{
                  backgroundColor: "#fff",
                  color: "#186A81",
                  fontWeight: "bold",
                  px: 4,
                  "&:hover": { backgroundColor: "#E8F4F6" },
                }}
              >
                <Icon sx={{ mr: 1 }}>email</Icon>
                Nous contacter
              </MDButton>
            </Card>
          </MDBox>
        </Container>

        <MDBox textAlign="center" mt={8}>
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.5 }}>
            &copy; 2026 ConformIMF - Guy Modeste EHOULO - Tous droits réservés
          </MDTypography>
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default Documentation;
